import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { unstable_usePrompt, useBeforeUnload } from 'react-router-dom';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import {
  addDoc,
  collection,
  deleteField,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  setDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import {
  Ban,
  Check,
  ChevronDown,
  Copy,
  Globe,
  ImagePlus,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { AdminHeader } from '../../components/admin/AdminHeader';
import { AdminNav } from '../../components/admin/AdminNav';
import { AdminLoginCard } from '../../components/admin/AdminLoginCard';
import { ProductPageBackgroundSettings } from '../../components/admin/ProductPageBackgroundSettings';
import { FoodProductStoryEditor } from '../../components/admin/FoodProductStoryEditor';
import { HouseholdProductStoryEditor } from '../../components/admin/HouseholdProductStoryEditor';
import { ProductMarketingEditor } from '../../components/admin/ProductMarketingEditor';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useToast } from '../../hooks/useToast';
import {
  categories,
  createEmptyBodycareDetails,
  createEmptyFoodDetails,
  createEmptyHouseholdDetails,
  createEmptyNutrition,
  defaultProducts,
  getProductAttributeLabel,
  normalizeBodycareDetails,
  normalizeFoodDetails,
  normalizeHouseholdDetails,
  normalizeNutrition,
  normalizeProduct,
  normalizeProductCategory,
  normalizeProductImageFocus,
  normalizeProductGallery,
  normalizeProductSubcategory,
  subcategoryOptionsByCategory,
} from '../../data/products';
import { storage } from '../../firebase';
import { db } from '../../firebase';
import { getStockMap, saveStockMap } from '../../utils/adminStorage';

const UNCATEGORIZED_ID = 'uncategorized';
const PRODUCT_GROUP_BY_CATEGORY = 'category';
const PRODUCT_GROUP_BY_SUPPLIER = 'supplier';
const AGREEMENT_STATUSES = [
  'Lehetséges szállító',
  'Felhívni',
  'Emailt írni',
  'Megegyezés alatt',
  'Termék tesztelés alatt',
  'Kész',
];
const EMPTY_SUPPLIER_FORM = {
  name: '',
  website: '',
  contact: '',
  status: AGREEMENT_STATUSES[0],
  comment: '',
};
const agreementSelectClass =
  'w-full appearance-none rounded-xl border border-border bg-secondary px-4 py-2 text-xs font-medium text-foreground transition-colors hover:bg-secondary/70 focus:outline-none focus:ring-2 focus:ring-primary/20';
const categoryOptions = [
  { value: UNCATEGORIZED_ID, label: 'Nincs kategória' },
  ...categories.map((category) => ({
    value: category.id,
    label: category.name,
  })),
];

const slugify = (value) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const stripCopyLabel = (value) =>
  (value || '')
    .replace(/\b(másolat|masolat)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

const createIdFromName = (name, existingIds, options = {}) => {
  const source = options.stripCopyLabel ? stripCopyLabel(name) : name;
  const base = slugify(source || '') || 'uj-termek';
  let id = base;
  let counter = 2;
  while (existingIds.has(id)) {
    id = `${base}-${counter}`;
    counter += 1;
  }
  return id;
};

const createNewProduct = ({ id, name }) => ({
  id,
  name,
  price: 0,
  shortDescription: '',
  longDescription: '',
  category: UNCATEGORIZED_ID,
  subcategory: '',
  isActive: true,
  isHungarian: false,
  sugarFree: false,
  isBestseller: false,
  image: '',
  galleryImages: [],
  imageFocusX: 50,
  imageFocusY: 50,
  flavorNotes: [],
  ingredients: [],
  nutrition: createEmptyNutrition(),
  storage: '',
  usage: '',
  foodDetails: createEmptyFoodDetails(),
  householdDetails: createEmptyHouseholdDetails(),
  bodycareDetails: createEmptyBodycareDetails(),
});

const toFormState = (product) => {
  const { x: imageFocusX, y: imageFocusY } = normalizeProductImageFocus(
    product?.imageFocusX,
    product?.imageFocusY,
  );
  const foodDetails = normalizeFoodDetails(product.foodDetails);
  const migratedFoodBenefits = [...foodDetails.benefits];
  if (foodDetails.didYouKnow) {
    if (migratedFoodBenefits.length < 4) {
      migratedFoodBenefits.push({
        title: 'Érdekesség',
        description: foodDetails.didYouKnow,
      });
    } else {
      const lastIndex = migratedFoodBenefits.length - 1;
      migratedFoodBenefits[lastIndex] = {
        ...migratedFoodBenefits[lastIndex],
        description: [migratedFoodBenefits[lastIndex].description, foodDetails.didYouKnow]
          .filter(Boolean)
          .join('\n\n'),
      };
    }
  }
  const householdDetails = normalizeHouseholdDetails(product.householdDetails);
  const bodycareDetails = normalizeBodycareDetails(product.bodycareDetails);
  return {
    id: product.id || '',
    name: product.name || '',
    price: product.price ?? 0,
    category: normalizeProductCategory(product.category) || UNCATEGORIZED_ID,
    subcategory: normalizeProductSubcategory(product.category, product.subcategory),
    image: product.image || '',
    galleryImages: normalizeProductGallery(product.galleryImages),
    imageFocusX,
    imageFocusY,
    shortDescription: product.shortDescription || '',
    longDescription: product.longDescription || '',
    flavorNotes: (product.flavorNotes || []).join(', '),
    ingredients: (product.ingredients || []).join(', '),
    nutrition: normalizeNutrition(product.nutrition),
    storage: product.storage || '',
    usage: product.usage || '',
    foodDetails: {
      ...foodDetails,
      benefits: migratedFoodBenefits,
      didYouKnow: '',
      allergens: foodDetails.allergens.join(', '),
      dietaryTags: foodDetails.dietaryTags.join(', '),
      usageIdeas: foodDetails.usageIdeas.join(', '),
      excludedIngredients: foodDetails.excludedIngredients.join(', '),
    },
    householdDetails: {
      ...householdDetails,
      suitableFor: householdDetails.suitableFor.join(', '),
      ecoFeatures: householdDetails.ecoFeatures.join(', '),
      excludedIngredients: householdDetails.excludedIngredients.join(', '),
    },
    bodycareDetails: {
      ...bodycareDetails,
      suitableFor: bodycareDetails.suitableFor.join(', '),
      skinHairTypes: bodycareDetails.skinHairTypes.join(', '),
      activeIngredients: bodycareDetails.activeIngredients.join(', '),
      certifications: bodycareDetails.certifications.join(', '),
    },
    isActive: product.isActive ?? true,
    isHungarian: Boolean(product.isHungarian),
    sugarFree: Boolean(product.sugarFree),
    isBestseller: Boolean(product.isBestseller),
  };
};

const parseList = (value) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const sortProductsByActiveAndName = (products) =>
  [...products].sort((a, b) => {
    const aActive = a.isActive !== false;
    const bActive = b.isActive !== false;
    if (aActive !== bActive) return aActive ? -1 : 1;
    return a.name.localeCompare(b.name, 'hu');
  });

const getImageUploadErrorMessage = (uploadError) => {
  const code = String(uploadError?.code || '').trim();
  if (code === 'storage/unauthorized' || code === 'storage/unauthenticated') {
    return 'Nincs jogosultságod a kép feltöltéséhez. Ellenőrizd a Storage szabályokat és a bejelentkezést.';
  }
  if (code === 'storage/no-default-bucket') {
    return 'Hiányzik vagy hibás a Storage bucket beállítás (`REACT_APP_FIREBASE_STORAGE_BUCKET`).';
  }
  if (code === 'storage/quota-exceeded') {
    return 'A Storage kvóta elfogyott. Ellenőrizd a Firebase Storage kvótát.';
  }
  if (code === 'storage/invalid-format') {
    return 'A kiválasztott fájl formátuma nem támogatott.';
  }
  if (code === 'storage/retry-limit-exceeded' || code === 'storage/network-request-failed') {
    return 'Hálózati hiba történt feltöltés közben. Próbáld újra.';
  }
  return `Nem sikerült feltölteni a képet. (Hiba: ${code || 'ismeretlen'})`;
};

const toSupplierProductMap = (items) => {
  const map = new Map();
  if (!Array.isArray(items)) return map;
  items.forEach((item) => {
    const productId = String(item?.productId || '').trim();
    if (!productId || map.has(productId)) return;
    map.set(productId, {
      productId,
      productName: String(item?.productName || '').trim(),
    });
  });
  return map;
};

const toSupplierProductArray = (map) =>
  Array.from(map.values()).map((item) => ({
    productId: item.productId,
    productName: String(item.productName || ''),
  }));

export default function AdminProducts() {
  const { user, loading } = useAdminAuth();
  const { toast } = useToast();
  const [productsList, setProductsList] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [groupBy, setGroupBy] = useState(PRODUCT_GROUP_BY_SUPPLIER);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState('');
  const [isFirestoreEmpty, setIsFirestoreEmpty] = useState(false);
  const [dirtyMap, setDirtyMap] = useState({});
  const [isProductPageBackgroundDirty, setIsProductPageBackgroundDirty] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [supplierForm, setSupplierForm] = useState(EMPTY_SUPPLIER_FORM);
  const [supplierProductQuery, setSupplierProductQuery] = useState('');
  const [selectedSupplierProducts, setSelectedSupplierProducts] = useState([]);
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [editSupplierForm, setEditSupplierForm] = useState(EMPTY_SUPPLIER_FORM);
  const [editSupplierProductQuery, setEditSupplierProductQuery] = useState('');
  const [editSelectedSupplierProducts, setEditSelectedSupplierProducts] = useState([]);
  const [editSupplierBaseline, setEditSupplierBaseline] = useState({
    form: EMPTY_SUPPLIER_FORM,
    products: [],
  });

  const groupedProducts = useMemo(() => {
    if (groupBy === PRODUCT_GROUP_BY_SUPPLIER) {
      const productsById = new Map(productsList.map((product) => [product.id, product]));
      const assignedProductIds = new Set();
      const groups = [];
      suppliers.forEach((supplier) => {
        const supplierId = String(supplier?.id || '').trim();
        if (!supplierId) return;
        const supplierName = String(supplier?.name || '').trim() || 'Névtelen szállító';
        const supplierProducts = toSupplierProductMap(supplier?.products);
        const products = Array.from(supplierProducts.keys())
          .map((productId) => productsById.get(productId))
          .filter(Boolean);

        products.forEach((product) => assignedProductIds.add(product.id));

        groups.push({
          id: `supplier-${supplierId}`,
          name: supplierName,
          hint: supplierId,
          supplier,
          products: sortProductsByActiveAndName(products),
        });
      });

      const unassignedProducts = productsList.filter((product) => !assignedProductIds.has(product.id));
      if (unassignedProducts.length > 0) {
        groups.push({
          id: 'supplier-unassigned',
          name: 'Nincs szállítóhoz rendelve',
          hint: 'unassigned',
          products: sortProductsByActiveAndName(unassignedProducts),
        });
      }

      groups.sort((a, b) => {
        const aUnassigned = a.id === 'supplier-unassigned';
        const bUnassigned = b.id === 'supplier-unassigned';
        if (aUnassigned !== bUnassigned) return aUnassigned ? 1 : -1;
        const aInactive = a.supplier?.isActive === false;
        const bInactive = b.supplier?.isActive === false;
        if (aInactive !== bInactive) return aInactive ? 1 : -1;
        const aPossible = (a.supplier?.status || '') === 'Lehetséges szállító';
        const bPossible = (b.supplier?.status || '') === 'Lehetséges szállító';
        if (aPossible !== bPossible) return aPossible ? 1 : -1;
        return a.name.localeCompare(b.name, 'hu');
      });

      return groups;
    }

    const categoryOrder = categories.map((category) => category.id);
    const categoryNameMap = new Map(categories.map((category) => [category.id, category.name]));
    const groups = new Map();

    productsList.forEach((product) => {
      const rawCategory = product.category || UNCATEGORIZED_ID;
      const isUncategorized = rawCategory === UNCATEGORIZED_ID;
      const categoryId = rawCategory || UNCATEGORIZED_ID;
      const categoryName = isUncategorized
        ? 'Új termékek'
        : categoryNameMap.get(categoryId) || product.category || 'Egyéb';
      if (!groups.has(categoryId)) {
        groups.set(categoryId, { id: categoryId, name: categoryName, products: [] });
      }
      groups.get(categoryId).products.push(product);
    });

    const list = Array.from(groups.values()).map((group) => ({
      ...group,
      hint: group.id,
      products: sortProductsByActiveAndName(group.products),
    }));

    list.sort((a, b) => {
      if (a.id === UNCATEGORIZED_ID && b.id !== UNCATEGORIZED_ID) return -1;
      if (b.id === UNCATEGORIZED_ID && a.id !== UNCATEGORIZED_ID) return 1;
      const aIndex = categoryOrder.indexOf(a.id);
      const bIndex = categoryOrder.indexOf(b.id);
      if (aIndex === -1 && bIndex === -1) {
        return a.name.localeCompare(b.name, 'hu');
      }
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    return list;
  }, [groupBy, productsList, suppliers]);

  const existingIds = useMemo(
    () => new Set(productsList.map((product) => product.id)),
    [productsList],
  );

  const isNewSupplierDirty = useMemo(() => {
    if (!showSupplierForm) return false;
    const hasFormValues =
      supplierForm.name.trim()
      || supplierForm.website.trim()
      || supplierForm.contact.trim()
      || supplierForm.comment.trim()
      || supplierForm.status !== AGREEMENT_STATUSES[0];
    return Boolean(hasFormValues || selectedSupplierProducts.length > 0);
  }, [selectedSupplierProducts, showSupplierForm, supplierForm]);

  const isEditSupplierDirty = useMemo(() => {
    if (!editingSupplierId) return false;
    return JSON.stringify(editSupplierForm) !== JSON.stringify(editSupplierBaseline.form)
      || JSON.stringify(editSelectedSupplierProducts) !== JSON.stringify(editSupplierBaseline.products);
  }, [editSelectedSupplierProducts, editSupplierBaseline, editSupplierForm, editingSupplierId]);

  const hasDirty = useMemo(
    () => Object.values(dirtyMap).some(Boolean)
      || isNewSupplierDirty
      || isEditSupplierDirty
      || isProductPageBackgroundDirty,
    [dirtyMap, isEditSupplierDirty, isNewSupplierDirty, isProductPageBackgroundDirty],
  );

  const copySupplierAssignments = useCallback(async (sourceProductId, targetProductId, targetProductName) => {
    if (!sourceProductId || !targetProductId) return;
    const suppliersSnapshot = await getDocs(collection(db, 'suppliers'));
    const updates = [];

    suppliersSnapshot.forEach((supplierSnap) => {
      const productsMap = toSupplierProductMap(supplierSnap.data()?.products);
      if (!productsMap.has(sourceProductId) || productsMap.has(targetProductId)) return;

      productsMap.set(targetProductId, {
        productId: targetProductId,
        productName: String(targetProductName || '').trim() || targetProductId,
      });

      updates.push(
        setDoc(
          doc(db, 'suppliers', supplierSnap.id),
          {
            products: toSupplierProductArray(productsMap),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        ),
      );
    });

    if (updates.length > 0) {
      await Promise.all(updates);
    }
  }, []);

  const replaceSupplierProductId = useCallback(async (sourceProductId, targetProductId, targetProductName) => {
    if (!sourceProductId || !targetProductId || sourceProductId === targetProductId) return;
    const suppliersSnapshot = await getDocs(collection(db, 'suppliers'));
    const updates = [];

    suppliersSnapshot.forEach((supplierSnap) => {
      const productsMap = toSupplierProductMap(supplierSnap.data()?.products);
      if (!productsMap.has(sourceProductId)) return;

      const sourceEntry = productsMap.get(sourceProductId);
      productsMap.delete(sourceProductId);

      if (!productsMap.has(targetProductId)) {
        productsMap.set(targetProductId, {
          productId: targetProductId,
          productName:
            String(targetProductName || '').trim()
            || String(sourceEntry?.productName || '').trim()
            || targetProductId,
        });
      } else if (String(targetProductName || '').trim()) {
        const existing = productsMap.get(targetProductId);
        productsMap.set(targetProductId, {
          ...existing,
          productName: String(targetProductName || '').trim(),
        });
      }

      updates.push(
        setDoc(
          doc(db, 'suppliers', supplierSnap.id),
          {
            products: toSupplierProductArray(productsMap),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        ),
      );
    });

    if (updates.length > 0) {
      await Promise.all(updates);
    }
  }, []);

  const handleDirtyChange = useCallback((id, isDirty) => {
    setDirtyMap((prev) => {
      if (prev[id] === isDirty) return prev;
      return { ...prev, [id]: isDirty };
    });
  }, []);

  useEffect(() => {
    setDirtyMap((prev) => {
      const ids = new Set(productsList.map((product) => product.id));
      const next = {};
      Object.keys(prev).forEach((id) => {
        if (ids.has(id)) next[id] = prev[id];
      });
      return next;
    });
  }, [productsList]);

  unstable_usePrompt({
    when: hasDirty,
    message:
      'Nem mentett módosítások vannak. Szeretnéd menteni? A Mégse gombbal itt maradsz.',
  });

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasDirty) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [hasDirty],
    ),
  );

  useEffect(() => {
    if (!user) return undefined;
    const unsub = onSnapshot(
      collection(db, 'products'),
      (snapshot) => {
        setIsFirestoreEmpty(snapshot.empty);
        if (snapshot.empty) {
          setProductsList(defaultProducts);
        } else {
          const next = snapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            return normalizeProduct({ ...data, id: data.id || docSnap.id });
          });
          setProductsList(next);
        }
        setError('');
      },
      () => {
        setProductsList(defaultProducts);
        setError('Nem sikerült betölteni a termékeket a Firestore-ból.');
        setIsFirestoreEmpty(true);
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSuppliers([]);
      return undefined;
    }
    const unsub = onSnapshot(
      collection(db, 'suppliers'),
      (snapshot) => {
        const next = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setSuppliers(next);
      },
      (snapshotError) => {
        console.error('Suppliers snapshot failed:', snapshotError);
        setSuppliers([]);
        setError('Nem sikerült betölteni a szállítókat a Firestore-ból.');
      },
    );
    return () => unsub();
  }, [user]);

  const filterSupplierProducts = useCallback((queryValue) => {
    const normalizedQuery = queryValue.trim().toLowerCase();
    const list = [...productsList].sort((a, b) => a.name.localeCompare(b.name, 'hu'));
    if (!normalizedQuery) return list;
    return list.filter((product) => `${product.name} ${product.id}`.toLowerCase().includes(normalizedQuery));
  }, [productsList]);

  const filteredSupplierProducts = useMemo(
    () => filterSupplierProducts(supplierProductQuery),
    [filterSupplierProducts, supplierProductQuery],
  );

  const filteredEditSupplierProducts = useMemo(
    () => filterSupplierProducts(editSupplierProductQuery),
    [editSupplierProductQuery, filterSupplierProducts],
  );

  const handleOpenNewSupplier = () => {
    setGroupBy(PRODUCT_GROUP_BY_SUPPLIER);
    if (showSupplierForm) {
      resetNewSupplierForm();
    } else {
      setShowSupplierForm(true);
    }
  };

  const handleSupplierFormChange = (event) => {
    const { name, value } = event.target;
    setSupplierForm((current) => ({ ...current, [name]: value }));
  };

  const handleEditSupplierFormChange = (event) => {
    const { name, value } = event.target;
    setEditSupplierForm((current) => ({ ...current, [name]: value }));
  };

  const handleAddSelectedSupplierProduct = (product, isEditing = false) => {
    const setter = isEditing ? setEditSelectedSupplierProducts : setSelectedSupplierProducts;
    setter((current) => {
      if (current.some((item) => item.productId === product.id)) return current;
      return [...current, { productId: product.id, productName: product.name }];
    });
  };

  const handleRemoveSelectedSupplierProduct = (productId, isEditing = false) => {
    const setter = isEditing ? setEditSelectedSupplierProducts : setSelectedSupplierProducts;
    setter((current) => current.filter((item) => item.productId !== productId));
  };

  const resetNewSupplierForm = () => {
    setSupplierForm(EMPTY_SUPPLIER_FORM);
    setSelectedSupplierProducts([]);
    setSupplierProductQuery('');
    setShowSupplierForm(false);
  };

  const handleSaveSupplier = async (event) => {
    event.preventDefault();
    setError('');
    const name = supplierForm.name.trim();
    if (!name) {
      setError('A szállító neve kötelező.');
      return;
    }
    try {
      await addDoc(collection(db, 'suppliers'), {
        name,
        website: supplierForm.website.trim(),
        contact: supplierForm.contact.trim(),
        status: supplierForm.status,
        isActive: true,
        ...(selectedSupplierProducts.length > 0 ? { products: selectedSupplierProducts } : {}),
        comment: supplierForm.comment.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      resetNewSupplierForm();
    } catch {
      setError('Nem sikerült elmenteni a szállítót.');
    }
  };

  const handleStatusChange = async (supplierId, nextStatus) => {
    try {
      await updateDoc(doc(db, 'suppliers', supplierId), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
      });
    } catch {
      setError('Nem sikerült frissíteni a szállító státuszát.');
    }
  };

  const handleStartEditSupplier = (supplier) => {
    const nextForm = {
      name: supplier.name || '',
      website: supplier.website || '',
      contact: supplier.contact || '',
      status: supplier.status || AGREEMENT_STATUSES[0],
      comment: supplier.comment || '',
    };
    const nextProducts = Array.isArray(supplier.products)
      ? supplier.products.map((item) => ({
          productId: item.productId,
          productName:
            item.productName
            || productsList.find((product) => product.id === item.productId)?.name
            || item.productId,
        }))
      : [];
    setEditingSupplierId(supplier.id);
    setEditSupplierForm(nextForm);
    setEditSelectedSupplierProducts(nextProducts);
    setEditSupplierBaseline({ form: nextForm, products: nextProducts });
    setEditSupplierProductQuery('');
    setExpandedGroupId(`supplier-${supplier.id}`);
  };

  const handleCancelEditSupplier = () => {
    setEditingSupplierId(null);
    setEditSupplierForm(EMPTY_SUPPLIER_FORM);
    setEditSelectedSupplierProducts([]);
    setEditSupplierBaseline({ form: EMPTY_SUPPLIER_FORM, products: [] });
    setEditSupplierProductQuery('');
  };

  const handleUpdateSupplier = async (event) => {
    event.preventDefault();
    if (!editingSupplierId) return;
    setError('');
    const name = editSupplierForm.name.trim();
    if (!name) {
      setError('A szállító neve kötelező.');
      return;
    }
    try {
      await updateDoc(doc(db, 'suppliers', editingSupplierId), {
        name,
        website: editSupplierForm.website.trim(),
        contact: editSupplierForm.contact.trim(),
        status: editSupplierForm.status,
        products: editSelectedSupplierProducts.length > 0
          ? editSelectedSupplierProducts
          : deleteField(),
        comment: editSupplierForm.comment.trim(),
        updatedAt: serverTimestamp(),
      });
      handleCancelEditSupplier();
    } catch {
      setError('Nem sikerült frissíteni a szállítót.');
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('Biztosan törlöd ezt a szállítót?')) return;
    try {
      await deleteDoc(doc(db, 'suppliers', supplierId));
      if (editingSupplierId === supplierId) handleCancelEditSupplier();
    } catch {
      setError('Nem sikerült törölni a szállítót.');
    }
  };

  const handleSetSupplierActive = async (supplierId, nextIsActive) => {
    try {
      await updateDoc(doc(db, 'suppliers', supplierId), {
        isActive: nextIsActive,
        updatedAt: serverTimestamp(),
      });
    } catch {
      setError('Nem sikerült frissíteni a szállító aktív állapotát.');
    }
  };

  const openWebsite = (rawUrl) => {
    const trimmedUrl = rawUrl?.trim();
    if (!trimmedUrl) return;
    const url = /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleAdd = async () => {
    const baseName = 'Új termék';
    const nextId = createIdFromName(baseName, existingIds);
    const nextProduct = createNewProduct({ id: nextId, name: baseName });
    try {
      const docRef = doc(db, 'products', nextProduct.id);
      await setDoc(docRef, {
        ...nextProduct,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setExpandedId(nextProduct.id);
    } catch {
      setError('Nem sikerült létrehozni az új terméket.');
    }
  };

  const handleSeed = async () => {
    if (!window.confirm('Feltöltöd az alap termékeket a Firestore-ba?')) return;
    try {
      await Promise.all(
        defaultProducts.map((product) =>
          setDoc(
            doc(db, 'products', product.id),
            {
              ...product,
              id: product.id,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          ),
        ),
      );
    } catch {
      setError('Nem sikerült feltölteni az alap termékeket.');
    }
  };

  const handleSave = async (id, nextProduct) => {
    const nextId = nextProduct.id;
    if (nextId !== id) {
      const newRef = doc(db, 'products', nextId);
      const existing = await getDoc(newRef);
      if (existing.exists()) {
        window.alert('Ez az ID már létezik. Kérlek válassz egyedi ID-t.');
        return;
      }
    }

    try {
      await setDoc(
        doc(db, 'products', nextId),
        {
          ...nextProduct,
          id: nextId,
          updatedAt: serverTimestamp(),
          createdAt: nextProduct.createdAt || serverTimestamp(),
        },
        { merge: true },
      );

      if (nextId !== id) {
        await replaceSupplierProductId(id, nextId, nextProduct.name);
        await deleteDoc(doc(db, 'products', id));
        const stock = getStockMap();
        if (stock[id] !== undefined) {
          const nextStock = { ...stock, [nextId]: stock[id] };
          delete nextStock[id];
          saveStockMap(nextStock);
        }
      }
      setDirtyMap((prev) => {
        const next = { ...prev, [nextId]: false };
        if (id !== nextId) {
          delete next[id];
        }
        return next;
      });
      toast({
        title: 'Termék mentve',
        description: nextProduct.name?.trim() || 'A módosítások elmentve.',
      });
    } catch {
      setError('Nem sikerült menteni a terméket.');
    }
  };

  const handleDuplicate = async (draft, sourceProductId = '') => {
    const baseName = draft?.name?.trim() || 'Új termék';
    const copyName = `${baseName} másolat`;
    const tempId = `draft-${Date.now().toString(36)}`;
    const { createdAt, updatedAt, ...rest } = draft || {};
    const copy = {
      ...rest,
      id: tempId,
      name: copyName,
      autoId: true,
    };

    try {
      await setDoc(
        doc(db, 'products', tempId),
        {
          ...copy,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      const sourceId = String(sourceProductId || draft?.id || '').trim();
      if (sourceId) {
        await copySupplierAssignments(sourceId, tempId, copy.name);
      }
      setExpandedId(tempId);
      setDirtyMap((prev) => ({ ...prev, [tempId]: false }));
      toast({
        title: 'Termék másolva',
        description: copy.name,
      });
    } catch {
      setError('Nem sikerült lemásolni a terméket.');
    }
  };

  const handleActiveToggle = async (id, nextValue) => {
    try {
      await setDoc(
        doc(db, 'products', id),
        {
          isActive: nextValue,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setDirtyMap((prev) => ({ ...prev, [id]: false }));
      return true;
    } catch {
      setError('Nem sikerült frissíteni az aktív állapotot.');
      return false;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Biztosan törlöd ezt a terméket?')) return;
    try {
      await deleteDoc(doc(db, 'products', id));
    } catch {
      setError('Nem sikerült törölni a terméket.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <div className="rounded-3xl border border-[#0F3D2C] p-8 md:p-10 bg-card/60">
              <p className="text-sm text-muted-foreground">Betöltés...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">Admin / Termékek</h1>
            <AdminLoginCard description="A termékek kezeléséhez jelentkezz be Google fiókkal." />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="section-padding">
        <div className="container-zinga">
          <AdminHeader
            title="Admin / Termékek"
            actions={
              <>
                {isFirestoreEmpty && (
                  <button onClick={handleSeed} className="btn-secondary text-sm">
                    Alap termékek feltöltése
                  </button>
                )}
                <button onClick={handleAdd} className="btn-secondary text-sm">
                  + Új termék
                </button>
                <button onClick={handleOpenNewSupplier} className="btn-secondary text-sm">
                  {showSupplierForm ? 'Szállító űrlap bezárása' : '+ Új szállító'}
                </button>
              </>
            }
          />
          <AdminNav />

          <div className="mt-8 space-y-4">
            <ProductPageBackgroundSettings onDirtyChange={setIsProductPageBackgroundDirty} />
            <div className="rounded-2xl border border-border/70 bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs font-medium text-muted-foreground">Csoportosítás</p>
                <div className="inline-flex rounded-xl border border-border/70 bg-secondary/30 p-1">
                  <button
                    type="button"
                    onClick={() => setGroupBy(PRODUCT_GROUP_BY_CATEGORY)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      groupBy === PRODUCT_GROUP_BY_CATEGORY
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Kategória
                  </button>
                  <button
                    type="button"
                    onClick={() => setGroupBy(PRODUCT_GROUP_BY_SUPPLIER)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      groupBy === PRODUCT_GROUP_BY_SUPPLIER
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Szállító
                  </button>
                </div>
              </div>
            </div>
            {error && (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                {error}
              </div>
            )}
            {showSupplierForm && (
              <SupplierForm
                idPrefix="supplier-new"
                title="Új szállító"
                form={supplierForm}
                selectedProducts={selectedSupplierProducts}
                filteredProducts={filteredSupplierProducts}
                productQuery={supplierProductQuery}
                onProductQueryChange={setSupplierProductQuery}
                onChange={handleSupplierFormChange}
                onAddProduct={(product) => handleAddSelectedSupplierProduct(product)}
                onRemoveProduct={(productId) => handleRemoveSelectedSupplierProduct(productId)}
                onSubmit={handleSaveSupplier}
                onCancel={resetNewSupplierForm}
                submitLabel="+ Szállító hozzáadása"
              />
            )}
            {groupedProducts.length === 0 ? (
              <div className="rounded-2xl border border-border/70 bg-card p-6 text-sm text-muted-foreground">
                {groupBy === PRODUCT_GROUP_BY_SUPPLIER
                  ? 'Nincs rögzített szállító vagy megjeleníthető termék.'
                  : 'Nincs megjeleníthető termék.'}
              </div>
            ) : (
              groupedProducts.map((group, index) => {
                const productEditors = group.products.map((product) => (
                  <ProductEditor
                    key={product.id}
                    product={product}
                    isExpanded={expandedId === product.id}
                    onToggle={() => setExpandedId(product.id === expandedId ? null : product.id)}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    onDirtyChange={handleDirtyChange}
                    onActiveToggle={handleActiveToggle}
                    onDuplicate={handleDuplicate}
                    existingIds={existingIds}
                  />
                ));

                if (groupBy === PRODUCT_GROUP_BY_SUPPLIER) {
                  return (
                    <SupplierGroup
                      key={group.id}
                      group={group}
                      isExpanded={expandedGroupId === group.id}
                      onToggle={() => setExpandedGroupId(expandedGroupId === group.id ? null : group.id)}
                      onStatusChange={handleStatusChange}
                      onOpenWebsite={openWebsite}
                      onSetActive={handleSetSupplierActive}
                      onStartEdit={handleStartEditSupplier}
                      onDelete={handleDeleteSupplier}
                      isEditing={editingSupplierId === group.supplier?.id}
                      editForm={editSupplierForm}
                      editSelectedProducts={editSelectedSupplierProducts}
                      filteredEditProducts={filteredEditSupplierProducts}
                      editProductQuery={editSupplierProductQuery}
                      onEditProductQueryChange={setEditSupplierProductQuery}
                      onEditChange={handleEditSupplierFormChange}
                      onAddEditProduct={(product) => handleAddSelectedSupplierProduct(product, true)}
                      onRemoveEditProduct={(productId) =>
                        handleRemoveSelectedSupplierProduct(productId, true)
                      }
                      onUpdate={handleUpdateSupplier}
                      onCancelEdit={handleCancelEditSupplier}
                    >
                      {productEditors}
                    </SupplierGroup>
                  );
                }

                return (
                  <details
                    key={group.id}
                    defaultOpen={index === 0}
                    className="rounded-3xl border border-border/70 bg-card p-6"
                  >
                    <summary className="cursor-pointer flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-semibold">{group.name}</p>
                        <p className="text-xs text-muted-foreground">{group.products.length} termék</p>
                      </div>
                      <span className="text-xs text-muted-foreground">{group.hint || group.id}</span>
                    </summary>
                    <div className="mt-6 space-y-4">{productEditors}</div>
                  </details>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function SupplierGroup({
  group,
  isExpanded,
  onToggle,
  onStatusChange,
  onOpenWebsite,
  onSetActive,
  onStartEdit,
  onDelete,
  isEditing,
  editForm,
  editSelectedProducts,
  filteredEditProducts,
  editProductQuery,
  onEditProductQueryChange,
  onEditChange,
  onAddEditProduct,
  onRemoveEditProduct,
  onUpdate,
  onCancelEdit,
  children,
}) {
  const supplier = group.supplier;

  if (!supplier) {
    return (
      <div className="rounded-3xl border border-border/70 bg-card">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          className="flex w-full items-center justify-between gap-3 p-6 text-left"
        >
          <div>
            <p className="font-display text-lg font-semibold">{group.name}</p>
            <p className="text-xs text-muted-foreground">{group.products.length} termék</p>
          </div>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>
        {isExpanded && <div className="space-y-4 border-t border-border/50 p-6">{children}</div>}
      </div>
    );
  }

  const statusClass =
    supplier.status === 'Kész'
      ? '!border-[#0F3D2C] !bg-[#0F3D2C] !text-white hover:!bg-[#0F3D2C]'
      : supplier.status === 'Lehetséges szállító'
        ? '!border-[#BFD9EE] !bg-[#E7F2FA] !text-[#1E4C6B] hover:!bg-[#D9EAF7]'
        : supplier.status === 'Megegyezés alatt'
          ? '!border-[#B9D9B7] !bg-[#E4F2E1] !text-[#0F3D2C] hover:!bg-[#D8EED3]'
          : '';

  const handleRowKeyDown = (event) => {
    if (event.currentTarget !== event.target) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      className={`overflow-hidden rounded-3xl border border-border/70 bg-card ${
        supplier.isActive === false ? 'opacity-65' : ''
      }`}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={onToggle}
        onKeyDown={handleRowKeyDown}
        className="grid cursor-pointer gap-4 p-5 transition-colors hover:bg-secondary/10 md:grid-cols-2 xl:grid-cols-[minmax(190px,1.2fr)_minmax(180px,1fr)_minmax(190px,1fr)_minmax(160px,1fr)_auto_auto] xl:items-center"
      >
        <div className="flex min-w-0 items-center gap-3">
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate font-display text-lg font-semibold">{group.name}</p>
              {supplier.isActive === false && (
                <Ban className="h-4 w-4 shrink-0 text-muted-foreground" aria-label="Inaktív" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">{group.products.length} termék</p>
          </div>
        </div>

        <div className="min-w-0">
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Elérhetőség
          </p>
          <p className="break-words text-sm">{supplier.contact || '—'}</p>
        </div>

        <div onClick={(event) => event.stopPropagation()}>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Megállapodás
          </p>
          <div className="relative">
            <select
              value={supplier.status || AGREEMENT_STATUSES[0]}
              onChange={(event) => onStatusChange(supplier.id, event.target.value)}
              className={`${agreementSelectClass} ${statusClass}`}
              aria-label={`${group.name} megállapodási státusza`}
            >
              {AGREEMENT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>

        <div className="min-w-0">
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Megjegyzés
          </p>
          <p className="whitespace-pre-line break-words text-sm text-muted-foreground">
            {supplier.comment || '—'}
          </p>
        </div>

        <div onClick={(event) => event.stopPropagation()}>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Honlap
          </p>
          <button
            type="button"
            onClick={() => onOpenWebsite(supplier.website)}
            className={`btn-secondary h-9 w-9 p-0 ${
              supplier.website
                ? '!border-rose-200 !bg-rose-200 !text-rose-900 hover:!bg-rose-300'
                : ''
            }`}
            disabled={!supplier.website}
            aria-label="Honlap megnyitása"
            title="Honlap megnyitása"
          >
            <Globe className="h-4 w-4" />
          </button>
        </div>

        <div onClick={(event) => event.stopPropagation()}>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Művelet
          </p>
          <div className="flex items-center gap-2">
            <label
              className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-border bg-card"
              title="Aktív szállító"
            >
              <input
                type="checkbox"
                checked={supplier.isActive !== false}
                onChange={(event) => onSetActive(supplier.id, event.target.checked)}
                className="h-4 w-4 accent-[#0F3D2C]"
                aria-label="Aktív szállító"
              />
            </label>
            <button
              type="button"
              onClick={() => onStartEdit(supplier)}
              className="btn-secondary h-9 w-9 p-0"
              aria-label="Szállító szerkesztése"
              title="Szerkesztés"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(supplier.id)}
              className="btn-secondary h-9 w-9 p-0 text-destructive"
              aria-label="Szállító törlése"
              title="Törlés"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {isEditing && (
        <SupplierForm
          embedded
          idPrefix={`supplier-edit-${supplier.id}`}
          title="Szállító szerkesztése"
          form={editForm}
          selectedProducts={editSelectedProducts}
          filteredProducts={filteredEditProducts}
          productQuery={editProductQuery}
          onProductQueryChange={onEditProductQueryChange}
          onChange={onEditChange}
          onAddProduct={onAddEditProduct}
          onRemoveProduct={onRemoveEditProduct}
          onSubmit={onUpdate}
          onCancel={onCancelEdit}
          submitLabel="Mentés"
        />
      )}

      {isExpanded && (
        <div className="space-y-4 border-t border-border/50 p-6">
          {group.products.length > 0 ? (
            children
          ) : (
            <p className="text-sm text-muted-foreground">Ehhez a szállítóhoz még nincs termék rendelve.</p>
          )}
        </div>
      )}
    </div>
  );
}

function SupplierForm({
  idPrefix,
  title,
  form,
  selectedProducts,
  filteredProducts,
  productQuery,
  onProductQueryChange,
  onChange,
  onAddProduct,
  onRemoveProduct,
  onSubmit,
  onCancel,
  submitLabel,
  embedded = false,
}) {
  return (
    <div
      className={
        embedded
          ? 'border-t border-border/50 bg-secondary/5 p-6'
          : 'rounded-3xl border border-border/70 bg-card p-6'
      }
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-semibold">{title}</h2>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onCancel} className="btn-secondary text-sm">
            Mégse
          </button>
          <button type="submit" form={idPrefix} className="btn-primary text-sm">
            {submitLabel}
          </button>
        </div>
      </div>
      <form id={idPrefix} onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label htmlFor={`${idPrefix}-name`} className="mb-1 block text-xs font-medium">
            Szállító neve
          </label>
          <input
            id={`${idPrefix}-name`}
            name="name"
            value={form.name}
            onChange={onChange}
            className="input-zinga"
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-website`} className="mb-1 block text-xs font-medium">
            Honlap linkje
          </label>
          <input
            id={`${idPrefix}-website`}
            name="website"
            value={form.website}
            onChange={onChange}
            className="input-zinga"
            placeholder="https://"
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-contact`} className="mb-1 block text-xs font-medium">
            Elérhetőség
          </label>
          <input
            id={`${idPrefix}-contact`}
            name="contact"
            value={form.contact}
            onChange={onChange}
            className="input-zinga"
            placeholder="Telefon, email"
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-comment`} className="mb-1 block text-xs font-medium">
            Megjegyzés
          </label>
          <input
            id={`${idPrefix}-comment`}
            name="comment"
            value={form.comment}
            onChange={onChange}
            className="input-zinga"
            placeholder="Pl. fizetési feltételek"
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-status`} className="mb-1 block text-xs font-medium">
            Megállapodás
          </label>
          <div className="relative">
            <select
              id={`${idPrefix}-status`}
              name="status"
              value={form.status}
              onChange={onChange}
              className={agreementSelectClass}
            >
              {AGREEMENT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-xs font-medium">Elérhető termékek</label>
          <div className="rounded-2xl border border-border/70 bg-secondary/20 p-4">
            <label
              htmlFor={`${idPrefix}-product-search`}
              className="mb-2 block text-xs font-medium text-muted-foreground"
            >
              Keresés a termékek között
            </label>
            <input
              id={`${idPrefix}-product-search`}
              type="text"
              value={productQuery}
              onChange={(event) => onProductQueryChange(event.target.value)}
              className="input-zinga"
              placeholder="Kezdj el gépelni..."
            />
            <div className="mt-4 max-h-44 space-y-2 overflow-y-auto pr-1">
              {filteredProducts.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nincs találat.</p>
              ) : (
                filteredProducts.map((product) => {
                  const isSelected = selectedProducts.some((item) => item.productId === product.id);
                  return (
                    <div
                      key={product.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-border/50 bg-background px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium">{product.name}</p>
                        <p className="text-xs text-muted-foreground">ID: {product.id}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onAddProduct(product)}
                        className="btn-secondary text-xs"
                        disabled={isSelected}
                      >
                        {isSelected ? 'Kiválasztva' : '+ Hozzáad'}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-4">
              <p className="mb-2 text-xs font-medium text-muted-foreground">Kiválasztott termékek</p>
              {selectedProducts.length === 0 ? (
                <p className="text-xs text-muted-foreground">Még nincs kiválasztott termék.</p>
              ) : (
                <div className="space-y-2">
                  {selectedProducts.map((item) => (
                    <div
                      key={item.productId}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/50 bg-background px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium">{item.productName}</p>
                        <p className="text-xs text-muted-foreground">ID: {item.productId}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemoveProduct(item.productId)}
                        className="btn-secondary text-xs text-destructive"
                      >
                        Törlés
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

function ProductEditor({
  product,
  isExpanded,
  onToggle,
  onSave,
  onDelete,
  onDirtyChange,
  onActiveToggle,
  onDuplicate,
  existingIds,
}) {
  const [form, setForm] = useState(() => toFormState(product));
  const [baselineForm, setBaselineForm] = useState(() => toFormState(product));
  const [isAutoId, setIsAutoId] = useState(Boolean(product.autoId));
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const subcategoryOptions = subcategoryOptionsByCategory[form.category] || [];
  const isFoodCategory = normalizeProductCategory(form.category) === 'foods';
  const isHouseholdCategory = normalizeProductCategory(form.category) === 'household';
  const isBodycareCategory = normalizeProductCategory(form.category) === 'bodycare';
  const sugarFreeLabel = getProductAttributeLabel({ category: form.category, sugarFree: true });
  const fileInputId = `product-image-${product.id}`;
  const galleryInputId = `product-gallery-${product.id}`;
  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baselineForm),
    [form, baselineForm],
  );

  useEffect(() => {
    const nextForm = toFormState(product);
    if (product.autoId) {
      nextForm.id = '';
    }
    setForm(nextForm);
    setBaselineForm(nextForm);
    setIsAutoId(Boolean(product.autoId));
  }, [product]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(product.id, isDirty);
    }
  }, [product.id, isDirty, onDirtyChange]);

  const getGeneratedId = (name) => {
    const ids = new Set(existingIds || []);
    ids.delete(product.id);
    return createIdFromName(name, ids, { stripCopyLabel: true });
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === 'id') {
      setIsAutoId(false);
      setForm((prev) => ({ ...prev, id: value }));
      return;
    }
    if (name === 'name') {
      setForm((prev) => {
        const next = { ...prev, name: value };
        if (isAutoId) {
          next.id = value ? getGeneratedId(value) : '';
        }
        return next;
      });
      return;
    }
    if (name === 'category') {
      setForm((prev) => {
        const nextCategory = normalizeProductCategory(value) || UNCATEGORIZED_ID;
        return {
          ...prev,
          category: nextCategory,
          subcategory: normalizeProductSubcategory(nextCategory, prev.subcategory),
          nutrition: nextCategory === 'foods' ? prev.nutrition : createEmptyNutrition(),
        };
      });
      return;
    }
    if (name === 'storage' && !isFoodCategory) {
      return;
    }
    if (name === 'usage' && !isHouseholdCategory) {
      return;
    }
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNutritionChange = (field, value) => {
    if (!isFoodCategory) return;
    setForm((prev) => ({
      ...prev,
      nutrition: {
        ...prev.nutrition,
        [field]: value,
      },
    }));
  };

  const handleCategoryDetailChange = (section, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleCategoryBenefitChange = (section, index, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        benefits: (prev[section].benefits || []).map((benefit, benefitIndex) => (
          benefitIndex === index ? { ...benefit, [field]: value } : benefit
        )),
      },
    }));
  };

  const handleAddCategoryBenefit = (section) => {
    setForm((prev) => {
      const benefits = prev[section].benefits || [];
      if (benefits.length >= 4) return prev;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          benefits: [...benefits, { title: '', description: '' }],
        },
      };
    });
  };

  const handleRemoveCategoryBenefit = (section, index) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        benefits: (prev[section].benefits || []).filter((_, benefitIndex) => benefitIndex !== index),
      },
    }));
  };

  const handleCategoryCollectionItemChange = (section, collectionName, index, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [collectionName]: (prev[section][collectionName] || []).map((item, itemIndex) => (
          itemIndex === index ? { ...item, [field]: value } : item
        )),
      },
    }));
  };

  const handleAddCategoryCollectionItem = (section, collectionName, emptyItem, limit) => {
    setForm((prev) => {
      const items = prev[section][collectionName] || [];
      if (items.length >= limit) return prev;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [collectionName]: [...items, emptyItem],
        },
      };
    });
  };

  const handleRemoveCategoryCollectionItem = (section, collectionName, index) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [collectionName]: (prev[section][collectionName] || []).filter((_, itemIndex) => itemIndex !== index),
      },
    }));
  };

  const handleImageFocusChange = (axis, value) => {
    setForm((prev) => {
      const nextX = axis === 'imageFocusX' ? value : prev.imageFocusX;
      const nextY = axis === 'imageFocusY' ? value : prev.imageFocusY;
      const { x, y } = normalizeProductImageFocus(nextX, nextY);
      return {
        ...prev,
        imageFocusX: x,
        imageFocusY: y,
      };
    });
  };

  const buildPayload = () => {
    const nextId = isAutoId
      ? getGeneratedId(form.name)
      : form.id.trim() || product.id;
    const normalizedCategory = normalizeProductCategory(form.category) || UNCATEGORIZED_ID;
    const normalizedSubcategory = normalizeProductSubcategory(
      normalizedCategory,
      form.subcategory,
    );
    const { x: imageFocusX, y: imageFocusY } = normalizeProductImageFocus(
      form.imageFocusX,
      form.imageFocusY,
    );
    const { autoId, ...productBase } = product;
    return {
      ...productBase,
      id: nextId,
      name: form.name.trim() || product.name,
      price: Number(form.price) || 0,
      category: normalizedCategory,
      subcategory: normalizedSubcategory,
      image: form.image.trim(),
      galleryImages: normalizeProductGallery(form.galleryImages),
      imageFocusX,
      imageFocusY,
      shortDescription: form.shortDescription.trim(),
      longDescription: form.longDescription.trim(),
      flavorNotes: parseList(form.flavorNotes),
      ingredients: parseList(form.ingredients),
      nutrition:
        normalizedCategory === 'foods'
          ? normalizeNutrition(form.nutrition)
          : createEmptyNutrition(),
      storage: normalizedCategory === 'foods' ? form.storage.trim() : '',
      usage: normalizedCategory === 'household' ? form.usage.trim() : '',
      foodDetails:
        normalizedCategory === 'foods'
          ? normalizeFoodDetails({
            ...form.foodDetails,
            allergens: parseList(form.foodDetails.allergens),
            dietaryTags: parseList(form.foodDetails.dietaryTags),
            usageIdeas: parseList(form.foodDetails.usageIdeas),
            excludedIngredients: parseList(form.foodDetails.excludedIngredients),
          })
          : createEmptyFoodDetails(),
      householdDetails:
        normalizedCategory === 'household'
          ? normalizeHouseholdDetails({
            ...form.householdDetails,
            suitableFor: parseList(form.householdDetails.suitableFor),
            ecoFeatures: parseList(form.householdDetails.ecoFeatures),
            excludedIngredients: parseList(form.householdDetails.excludedIngredients),
          })
          : createEmptyHouseholdDetails(),
      bodycareDetails:
        normalizedCategory === 'bodycare'
          ? normalizeBodycareDetails({
            ...form.bodycareDetails,
            suitableFor: parseList(form.bodycareDetails.suitableFor),
            skinHairTypes: parseList(form.bodycareDetails.skinHairTypes),
            activeIngredients: parseList(form.bodycareDetails.activeIngredients),
            certifications: parseList(form.bodycareDetails.certifications),
          })
          : createEmptyBodycareDetails(),
      isActive: Boolean(form.isActive),
      isHungarian: Boolean(form.isHungarian),
      sugarFree: form.sugarFree,
      isLimitedEdition: false,
      isBestseller: form.isBestseller,
    };
  };

  const handleActiveChange = async (event) => {
    const nextValue = event.target.checked;
    setForm((prev) => ({ ...prev, isActive: nextValue }));
    if (onActiveToggle) {
      const ok = await onActiveToggle(product.id, nextValue);
      if (ok) {
        setBaselineForm((prev) => ({ ...prev, isActive: nextValue }));
      } else {
        setForm((prev) => ({ ...prev, isActive: baselineForm.isActive }));
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSaveAction();
  };

  const handleSaveAction = () => {
    const next = buildPayload();
    onSave(product.id, next);
  };

  const handleDuplicate = () => {
    if (!onDuplicate) return;
    const draft = buildPayload();
    onDuplicate(draft, product.id);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
      const folder = (form.id || product.id || 'termek').toString().trim();
      const storageRef = ref(storage, `products/${folder}/${Date.now()}-${safeName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setForm((prev) => ({ ...prev, image: url }));
    } catch (uploadError) {
      console.error('Product image upload failed:', uploadError);
      setUploadError(getImageUploadErrorMessage(uploadError));
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    const availableSlots = Math.max(0, 6 - form.galleryImages.length);
    const selectedFiles = files.slice(0, availableSlots);
    if (!selectedFiles.length) {
      setUploadError('A galériában legfeljebb 6 kép lehet.');
      return;
    }

    setUploadError('');
    setUploading(true);
    try {
      const folder = (form.id || product.id || 'termek').toString().trim();
      const uploadedImages = await Promise.all(
        selectedFiles.map(async (file, index) => {
          const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
          const storageRef = ref(
            storage,
            `products/${folder}/gallery/${Date.now()}-${index}-${safeName}`,
          );
          const snapshot = await uploadBytes(storageRef, file);
          return getDownloadURL(snapshot.ref);
        }),
      );
      setForm((prev) => ({
        ...prev,
        galleryImages: normalizeProductGallery([...prev.galleryImages, ...uploadedImages]),
      }));
    } catch (galleryUploadError) {
      console.error('Product gallery upload failed:', galleryUploadError);
      setUploadError(getImageUploadErrorMessage(galleryUploadError));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveGalleryImage = (imageUrl) => {
    setForm((prev) => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((image) => image !== imageUrl),
    }));
  };

  const handleFoodCollectionItemChange = (collectionName, index, field, value) => {
    setForm((prev) => ({
      ...prev,
      foodDetails: {
        ...prev.foodDetails,
        [collectionName]: prev.foodDetails[collectionName].map((item, itemIndex) => (
          itemIndex === index ? { ...item, [field]: value } : item
        )),
      },
    }));
  };

  const handleAddFoodCollectionItem = (collectionName, emptyItem, limit) => {
    setForm((prev) => {
      const items = prev.foodDetails[collectionName] || [];
      if (items.length >= limit) return prev;
      return {
        ...prev,
        foodDetails: {
          ...prev.foodDetails,
          [collectionName]: [...items, emptyItem],
        },
      };
    });
  };

  const handleRemoveFoodCollectionItem = (collectionName, index) => {
    setForm((prev) => ({
      ...prev,
      foodDetails: {
        ...prev.foodDetails,
        [collectionName]: prev.foodDetails[collectionName].filter((_, itemIndex) => itemIndex !== index),
      },
    }));
  };

  const handleRecipeImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
      const folder = (form.id || product.id || 'termek').toString().trim();
      const storageRef = ref(
        storage,
        `products/${folder}/recipes/${Date.now()}-${index}-${safeName}`,
      );
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      handleFoodCollectionItemChange('recipeIdeas', index, 'image', url);
    } catch (recipeUploadError) {
      console.error('Recipe image upload failed:', recipeUploadError);
      setUploadError(getImageUploadErrorMessage(recipeUploadError));
    } finally {
      setUploading(false);
    }
  };

  return (
    <details
      open={isExpanded}
      className="rounded-2xl border border-border/70 bg-card p-6"
    >
      <summary
        className="cursor-pointer flex flex-wrap items-center justify-between gap-3"
        onClick={(event) => {
          event.preventDefault();
          onToggle();
        }}
        aria-expanded={isExpanded}
      >
        <div>
          <p className="font-medium">{product.name}</p>
          <p className="text-xs text-muted-foreground">
            {product.category} • {product.price.toLocaleString('hu-HU')} Ft
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-secondary text-xs px-2 py-1"
            title="Másolás"
            aria-label="Másolás"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleDuplicate();
            }}
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            type="button"
            className={`btn-secondary text-xs px-2 py-1 ${isDirty ? '' : 'opacity-50 cursor-not-allowed'}`}
            title={isDirty ? 'Mentés' : 'Nincs mentendő módosítás'}
            aria-label="Mentés"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (!isDirty) return;
              handleSaveAction();
            }}
            disabled={!isDirty}
          >
            <Save className="w-4 h-4" />
          </button>
          <button
            type="button"
            className="btn-secondary text-xs text-destructive px-2 py-1"
            title="Törlés"
            aria-label="Törlés"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onDelete(product.id);
            }}
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <label
            className="cursor-pointer"
            title="Aktív a webshopban"
            onClick={(event) => event.stopPropagation()}
          >
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleActiveChange}
              onClick={(event) => event.stopPropagation()}
              className="sr-only"
              aria-label="Aktív a webshopban"
            />
            <span
              className={`inline-flex h-6 w-6 items-center justify-center rounded border-2 transition-colors ${
                form.isActive
                  ? 'border-[#0F3D2C] bg-[#E7F0EB]'
                  : 'border-border bg-card hover:border-[#0F3D2C]/50'
              }`}
              aria-hidden="true"
            >
              {form.isActive ? <Check className="h-3.5 w-3.5 text-[#0F3D2C]" strokeWidth={3} /> : null}
            </span>
          </label>
        </div>
      </summary>

      <form onSubmit={handleSubmit} className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-12">
        <div className="sm:col-span-1 lg:col-span-6">
          <label className="block text-xs font-medium mb-1">Termék név</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            className="input-zinga"
          />
        </div>
        <div className="sm:col-span-1 lg:col-span-6">
          <label className="block text-xs font-medium mb-1">Rövid leírás (kártyán, név alatt)</label>
          <input
            name="shortDescription"
            value={form.shortDescription}
            onChange={handleChange}
            className="input-zinga"
            maxLength={140}
          />
        </div>
        <div className="sm:col-span-2 lg:col-span-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-[1.2fr_1.2fr_2fr_2fr]">
          <div>
            <label className="block text-xs font-medium mb-1">Kategória</label>
            <select name="category" value={form.category} onChange={handleChange} className="input-zinga">
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Alkategória</label>
            {subcategoryOptions.length > 0 ? (
              <select
                name="subcategory"
                value={form.subcategory}
                onChange={handleChange}
                className="input-zinga"
              >
                <option value="">Nincs alkategória</option>
                {subcategoryOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                name="subcategory"
                value={form.subcategory}
                onChange={handleChange}
                className="input-zinga"
                placeholder="Nincs előre definiált alkategória"
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Eladási ár (Ft)</label>
            <input
              name="price"
              type="number"
              value={form.price}
              onChange={handleChange}
              className="input-zinga"
              min="0"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">ID</label>
            <input
              name="id"
              value={form.id}
              onChange={handleChange}
              className="input-zinga"
            />
          </div>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <label className="block text-xs font-medium mb-2">Fotó</label>
          <div className="flex flex-col items-start gap-3">
            <input
              id={fileInputId}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="sr-only"
            />
            <input
              id={galleryInputId}
              type="file"
              accept="image/*"
              multiple
              onChange={handleGalleryUpload}
              className="sr-only"
            />
            <div className="w-full max-w-[10rem]">
              {form.image ? (
                <label
                  htmlFor={fileInputId}
                  className="group block w-full cursor-pointer"
                  title="Kép cseréje"
                >
                  <div className="relative w-full aspect-square rounded-xl overflow-hidden border border-border/70 bg-secondary/40">
                    <img
                      src={form.image}
                      alt={form.name}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: `${form.imageFocusX}% ${form.imageFocusY}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/25">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-card/90 opacity-0 transition-opacity group-hover:opacity-100">
                        <ImagePlus className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </label>
              ) : (
                <label
                  htmlFor={fileInputId}
                  className="btn-secondary inline-flex h-10 w-10 cursor-pointer items-center justify-center p-0"
                  title="Kép feltöltése"
                  aria-label="Kép feltöltése"
                >
                  <ImagePlus className="w-4 h-4" />
                </label>
              )}
            </div>
            <div className="w-full max-w-xs space-y-3">
              <div>
                <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                  <label htmlFor={`${fileInputId}-focus-x`} className="font-medium">
                    Kép fókusz X
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={form.imageFocusX}
                    onChange={(event) => handleImageFocusChange('imageFocusX', event.target.value)}
                    className="input-zinga h-7 w-20 px-2 py-1 text-right text-xs"
                    aria-label="Kép fókusz X százalék"
                  />
                </div>
                <input
                  id={`${fileInputId}-focus-x`}
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={form.imageFocusX}
                  onChange={(event) => handleImageFocusChange('imageFocusX', event.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                  <label htmlFor={`${fileInputId}-focus-y`} className="font-medium">
                    Kép fókusz Y
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={form.imageFocusY}
                    onChange={(event) => handleImageFocusChange('imageFocusY', event.target.value)}
                    className="input-zinga h-7 w-20 px-2 py-1 text-right text-xs"
                    aria-label="Kép fókusz Y százalék"
                  />
                </div>
                <input
                  id={`${fileInputId}-focus-y`}
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={form.imageFocusY}
                  onChange={(event) => handleImageFocusChange('imageFocusY', event.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            <div className="w-full">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-medium">További képek</span>
                <span className="text-[11px] text-muted-foreground">
                  {form.galleryImages.length}/6
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {form.galleryImages.map((imageUrl, index) => (
                  <div
                    key={imageUrl}
                    className="group relative aspect-square overflow-hidden rounded-lg border border-border/70 bg-secondary/40"
                  >
                    <img
                      src={imageUrl}
                      alt={`${form.name} – ${index + 2}. kép`}
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveGalleryImage(imageUrl)}
                      className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-card/95 text-destructive opacity-0 shadow-sm transition-opacity group-hover:opacity-100 focus:opacity-100"
                      aria-label={`${index + 2}. kép eltávolítása`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {form.galleryImages.length < 6 ? (
                  <label
                    htmlFor={galleryInputId}
                    className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-primary/35 bg-primary/[0.03] text-primary transition-colors hover:bg-primary/[0.07]"
                    title="További képek feltöltése"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="text-[10px] font-medium">Hozzáadás</span>
                  </label>
                ) : null}
              </div>
              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                Egyszerre több képet is kijelölhetsz. Ezek a termékoldal galériájában jelennek meg.
              </p>
            </div>
            {uploading && <span className="text-xs text-muted-foreground">Feltöltés...</span>}
            {uploadError && <span className="text-xs text-destructive">{uploadError}</span>}
          </div>
        </div>
        <div className="sm:col-span-2 lg:col-span-8 grid gap-4">
          <div className="grid gap-4 md:grid-cols-12">
            <div className={isFoodCategory || isHouseholdCategory ? 'md:col-span-5' : 'md:col-span-12'}>
              <label className="block text-xs font-medium mb-1">
                {isBodycareCategory ? 'Összetevők / INCI (vesszővel)' : 'Összetevők (vesszővel)'}
              </label>
              <input
                name="ingredients"
                value={form.ingredients}
                onChange={handleChange}
                className="input-zinga"
              />
            </div>
            {isFoodCategory ? (
              <div className="md:col-span-7">
                <label className="block text-xs font-medium mb-1">Tárolás</label>
                <input
                  name="storage"
                  value={form.storage}
                  onChange={handleChange}
                  className="input-zinga"
                />
              </div>
            ) : isHouseholdCategory ? (
              <div className="md:col-span-7">
                <label className="block text-xs font-medium mb-1">Használat</label>
                <input
                  name="usage"
                  value={form.usage}
                  onChange={handleChange}
                  className="input-zinga"
                />
              </div>
            ) : null}
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Hosszú leírás</label>
            <textarea
              name="longDescription"
              value={form.longDescription}
              onChange={handleChange}
              rows={3}
              className="input-zinga resize-none"
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" name="sugarFree" checked={form.sugarFree} onChange={handleChange} />
              {sugarFreeLabel}
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                name="isHungarian"
                checked={form.isHungarian}
                onChange={handleChange}
              />
              Hazai termék
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                name="isBestseller"
                checked={form.isBestseller}
                onChange={handleChange}
              />
              Népszerű
            </label>
          </div>
        </div>
        {isFoodCategory ? (
          <FoodProductStoryEditor
            form={form}
            fileInputId={fileInputId}
            onDetailChange={handleCategoryDetailChange}
            onNutritionChange={handleNutritionChange}
            onCollectionItemChange={handleFoodCollectionItemChange}
            onAddCollectionItem={handleAddFoodCollectionItem}
            onRemoveCollectionItem={handleRemoveFoodCollectionItem}
            onRecipeImageUpload={handleRecipeImageUpload}
          />
        ) : null}
        {isHouseholdCategory ? (
          <HouseholdProductStoryEditor
            form={form}
            onDetailChange={handleCategoryDetailChange}
            onCollectionItemChange={handleCategoryCollectionItemChange}
            onAddCollectionItem={handleAddCategoryCollectionItem}
            onRemoveCollectionItem={handleRemoveCategoryCollectionItem}
          />
        ) : null}
        {isBodycareCategory ? (
          <fieldset className="sm:col-span-2 lg:col-span-12 rounded-2xl border border-[#D7B8A6]/50 bg-[#FAF1EC] p-5">
            <legend className="px-2 font-display text-base font-semibold text-[#754F3A]">
              Testápolási termék adatai
            </legend>
            <p className="mb-5 text-xs leading-relaxed text-muted-foreground">
              A vásárlói oldalon külön blokkban jelennek meg a bőr- vagy hajtípusok, a használat,
              a hatóanyagok és a minősítések.
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-xs font-medium">Nettó mennyiség / kiszerelés</label>
                <input
                  value={form.bodycareDetails.netQuantity}
                  onChange={(event) => handleCategoryDetailChange('bodycareDetails', 'netQuantity', event.target.value)}
                  className="input-zinga"
                  placeholder="pl. 80 g / 100 ml"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium">Állag / illat</label>
                <input
                  value={form.bodycareDetails.texture}
                  onChange={(event) => handleCategoryDetailChange('bodycareDetails', 'texture', event.target.value)}
                  className="input-zinga"
                  placeholder="pl. krémes, enyhén citrusos"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium">Mire használható? (vesszővel)</label>
                <input
                  value={form.bodycareDetails.suitableFor}
                  onChange={(event) => handleCategoryDetailChange('bodycareDetails', 'suitableFor', event.target.value)}
                  className="input-zinga"
                  placeholder="pl. arc, test, kéz"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium">Ajánlott bőr- / hajtípus (vesszővel)</label>
                <input
                  value={form.bodycareDetails.skinHairTypes}
                  onChange={(event) => handleCategoryDetailChange('bodycareDetails', 'skinHairTypes', event.target.value)}
                  className="input-zinga"
                  placeholder="pl. érzékeny bőr, száraz haj"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium">Kiemelt hatóanyagok (vesszővel)</label>
                <input
                  value={form.bodycareDetails.activeIngredients}
                  onChange={(event) => handleCategoryDetailChange('bodycareDetails', 'activeIngredients', event.target.value)}
                  className="input-zinga"
                  placeholder="pl. sheavaj, rozmaringolaj, E-vitamin"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium">Használat</label>
                <textarea
                  value={form.bodycareDetails.application}
                  onChange={(event) => handleCategoryDetailChange('bodycareDetails', 'application', event.target.value)}
                  className="input-zinga min-h-24 resize-y"
                  placeholder="Mennyit, milyen gyakran és hogyan kell használni?"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium">Minősítések és jellemzők (vesszővel)</label>
                <input
                  value={form.bodycareDetails.certifications}
                  onChange={(event) => handleCategoryDetailChange('bodycareDetails', 'certifications', event.target.value)}
                  className="input-zinga"
                  placeholder="pl. vegán, állatkísérlet-mentes, pálmaolajmentes"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-medium">Figyelmeztetések</label>
                <textarea
                  value={form.bodycareDetails.warnings}
                  onChange={(event) => handleCategoryDetailChange('bodycareDetails', 'warnings', event.target.value)}
                  className="input-zinga min-h-24 resize-y"
                  placeholder="Allergiapróba, szemkörnyék kerülése vagy egyéb fontos tudnivaló"
                />
              </div>
            </div>
          </fieldset>
        ) : null}
        {(isHouseholdCategory || isBodycareCategory) ? (
          <ProductMarketingEditor
            details={isHouseholdCategory ? form.householdDetails : form.bodycareDetails}
            section={isHouseholdCategory ? 'householdDetails' : 'bodycareDetails'}
            onDetailChange={handleCategoryDetailChange}
            onBenefitChange={handleCategoryBenefitChange}
            onAddBenefit={handleAddCategoryBenefit}
            onRemoveBenefit={handleRemoveCategoryBenefit}
          />
        ) : null}
      </form>
    </details>
  );
}
